import { Helmet } from "react-helmet";
import { CollectionFormTypes } from "../utils/schema";
import { useEffect, useMemo } from "react";
import { changeFavicon, changePageTitle, getImage } from "../utils/helpers";

interface Props {
  data: CollectionFormTypes;
}

export const SEO = ({ data }: Props) => {
  useEffect(() => {
    if (data?.seo?.favicon) {
      changeFavicon(data.seo.favicon);
    }
    if (data?.seo?.title) {
      changePageTitle(data.seo.title);
    }
  }, [data?.seo?.favicon, data?.seo?.title]);

  const canonicalUrl = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : ""),
    [],
  );

  const ogTitle = data.seo.ogTitle || data.seo.title;
  const ogDescription = data.seo.ogDescription || data.seo.description;
  const candidateImage = getImage(data.seo.ogImage, "/default-logo.png");
  const isFetchableByBots = (url: string) =>
    typeof url === "string" && /^(https?:)?\/\//.test(url);

  const ogImageUrl = isFetchableByBots(candidateImage)
    ? candidateImage
    : "/default-logo.png";

  const toAbsoluteUrl = (url: string) => {
    if (!url) return "";
    try {
      const base = typeof window !== "undefined" ? window.location.origin : "";
      return new URL(url, base || undefined).toString();
    } catch {
      return url;
    }
  };
  const ogImageAbsoluteUrl = toAbsoluteUrl(ogImageUrl);
  const metaKeywords = Array.isArray(data.seo.keywords)
    ? data.seo.keywords.join(", ")
    : "";

  return (
    <div>
      <Helmet>
        <title className="testify">{data.seo.title}</title>
        <link rel="canonical" href={canonicalUrl} />

        {/* Basic SEO */}
        <meta name="description" content={data.seo.description} />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={data.uniqueName} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImageAbsoluteUrl} />
        <meta property="og:url" content={canonicalUrl} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImageAbsoluteUrl} />
      </Helmet>
    </div>
  );
};
